import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Divider } from "@/components/catalyst/divider";
import dayjs from "dayjs";
import { Link } from "@/components/catalyst/link";
import { departingBookingsQuery } from "@/app/Dashboard/GraphQL/departingBookingsQuery";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { formatDateTimeFromNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { toDateTimeTzString } from "@/lib/Formatters/toDateTimeString";
import { IllustrationCalendar } from "@/assets/Illustrations";
import { useTranslations } from "use-intl";
import { useMemo } from "react";

export function BookingListStatBlock() {
  const t = useTranslations();
  const { supplierId } = useGlobalSupplier();
  const now = useMemo(() => toDateTimeTzString(dayjs().startOf("minute")), []);
  const { data } = useSuspenseGqlQuery(departingBookingsQuery, {
    supplierId: supplierId ?? undefined,
    now,
  });

  const bookings = data.bookings.data;

  return (
    <Card>
      <CardHeader className="space-y-0">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>{t("dashboard.blocks.upcomingBookings.title")}</CardTitle>
          <Link
            to="/$tenant/$orgId/supplier/{-$supplierId}/bookings"
            params={(prev) => prev}
            className="shrink-0 text-sm"
          >
            {t("dashboard.blocks.upcomingBookings.viewMore")}
          </Link>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {bookings.length > 0 ? (
          <div className="max-h-96 overflow-y-auto px-6 pb-6">
            {bookings.map((booking, index) => (
              <div key={booking.id}>
                {index > 0 ? <Divider soft className="my-3" /> : null}
                <Link
                  from="/$tenant/$orgId/supplier/{-$supplierId}"
                  to="/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"
                  params={(prev) => ({ ...prev, bookingId: booking.id })}
                  className="-mx-2 flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate text-sm font-medium">
                        {booking.name}
                      </div>
                      <div className="flex-shrink-0 text-xs text-gray-400">
                        #{booking.reference}
                      </div>
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      {booking.departureOffice?.name}{" "}
                      {t("dashboard.blocks.upcomingBookings.to")}{" "}
                      {booking.deliveryOffice?.name}
                      {" - "}
                      {booking.vehicle.name}
                    </div>
                  </div>
                  <div className="flex-shrink-0 whitespace-nowrap text-sm text-gray-600">
                    {formatDateTimeFromNow(
                      dayjs(booking.depart_at)
                        .tz(booking.supplier.timezone)
                        .toDate(),
                    )}
                  </div>
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-32 opacity-80">
              <IllustrationCalendar />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-900">
              {t("dashboard.blocks.upcomingBookings.empty")}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {t("dashboard.blocks.upcomingBookings.emptyDescription")}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
